// src/pages/portal/reporting/eod/EodSalesSections.js
// The sales half of the EOD page: what sold on this business day, and what it
// consumed from stock. Renders from the payload built by
// backend/app/services/eod_report_service.build_eod_report — the same object
// the receipt, PDF and CSV render from.
import React, { useState } from 'react';
import { C } from '../../../../styles/theme';
import { FONT, fmt, SectionHeader } from './EodCashSections';

// ─── Primitives ──────────────────────────────────────────────────────────────

const Card = ({ children, style }) => (
    <div style={{
        background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
        padding: 20, fontFamily: FONT, minWidth: 0, ...style,
    }}>
        {children}
    </div>
);

const SectionEmpty = ({ message }) => (
    <div style={{
        padding: '20px 4px', textAlign: 'center', fontSize: 13,
        color: C.mutedLight, fontFamily: FONT,
    }}>
        {message}
    </div>
);

const Th = ({ children, align = 'left' }) => (
    <th style={{
        textAlign: align, padding: '8px 10px', fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted,
        borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Td = ({ children, align = 'left', bold }) => (
    <td style={{
        textAlign: align, padding: '9px 10px', fontSize: 13,
        color: bold ? C.text : C.slate, fontWeight: bold ? 700 : 500,
        borderBottom: `1px solid ${C.borderLight}`,
    }}>
        {children}
    </td>
);

const Row = ({ children }) => (
    <tr
        onMouseEnter={(e) => { e.currentTarget.style.background = C.faint; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        style={{ transition: 'background 0.12s' }}
    >
        {children}
    </tr>
);

const Table = ({ children }) => (
    <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
            {children}
        </table>
    </div>
);

// Disclosure matching DenominationSection's affordance in EodCashSections, so
// the page has one collapse idiom rather than two.
const Disclosure = ({ label, open, onToggle }) => (
    <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
            fontWeight: 600, color: C.green, cursor: 'pointer', padding: '8px 0',
            fontFamily: FONT, userSelect: 'none',
        }}
    >
        <span style={{
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s', display: 'inline-block',
        }}>&#9654;</span>
        {label}
    </div>
);

// ─── Day summary ─────────────────────────────────────────────────────────────

const DaySummary = ({ totals, currency, t, isMobile }) => {
    const cells = [
        { label: t('transactions') || 'Transactions', value: String(totals.transactions ?? 0) },
        { label: t('gross_sales') || 'Gross Sales', value: fmt(totals.gross_sales, currency) },
        { label: t('discounts') || 'Discounts', value: fmt(totals.discounts, currency) },
        { label: t('total_expenses') || 'Expenses', value: fmt(totals.expenses, currency) },
        { label: t('till_total') || 'Till Total', value: fmt(totals.till_total, currency), strong: true },
    ];
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${cells.length}, 1fr)`,
            gap: 1, background: C.border, borderRadius: 10, overflow: 'hidden',
            marginBottom: 24, fontFamily: FONT,
        }}>
            {cells.map((cell, i) => (
                <div key={i} style={{
                    background: cell.strong ? C.greenLight : C.white,
                    padding: '14px 16px', textAlign: 'center', minWidth: 0,
                }}>
                    <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.6, color: C.muted, marginBottom: 6,
                    }}>{cell.label}</div>
                    <div style={{
                        fontSize: 17, fontWeight: 800,
                        color: cell.strong ? C.greenDark : C.text,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{cell.value}</div>
                </div>
            ))}
        </div>
    );
};

// ─── Sold: categories rollup + product table ─────────────────────────────────

const SoldSection = ({ products, categories, unattached, currency, t }) => {
    // Quantity descending by default — "what sold most" is the question this
    // page exists to answer.
    const [sortKey, setSortKey] = useState('quantity');
    // Add-ons stay collapsed until asked for: the table is a top-sellers list
    // first, and a burger with eleven add-ons would otherwise bury every other
    // product below the fold.
    const [openAddons, setOpenAddons] = useState({});
    const toggleAddons = (code) =>
        setOpenAddons(prev => ({ ...prev, [code]: !prev[code] }));

    const sorted = [...products].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    const totalQuantity = products.reduce((sum, p) => sum + (p.quantity || 0), 0);
    const totalRevenue = products.reduce((sum, p) => sum + (p.revenue || 0), 0);

    const SortButton = ({ value, label }) => (
        <button
            onClick={() => setSortKey(value)}
            style={{
                padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${sortKey === value ? C.green : C.border}`,
                background: sortKey === value ? C.greenLight : C.white,
                color: sortKey === value ? C.greenDark : C.muted,
                borderRadius: 6, fontFamily: FONT,
            }}
        >{label}</button>
    );

    return (
        <Card style={{ marginBottom: 24 }}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, flexWrap: 'wrap', marginBottom: 16,
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontFamily: FONT }}>
                    {t('sold') || 'Sold'}
                </div>
                {products.length > 0 && (
                    <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            alignSelf: 'center', marginRight: 4,
                        }}>{t('sort_by') || 'Sort by'}</span>
                        <SortButton value="quantity" label={t('quantity') || 'Quantity'} />
                        <SortButton value="revenue" label={t('revenue') || 'Revenue'} />
                    </div>
                )}
            </div>

            {products.length === 0 ? (
                <SectionEmpty message={t('eod_no_products_sold') || 'Nothing was sold on this business day.'} />
            ) : (
                <>
                    {/* Category rollup — these quantities sum the product rows below. */}
                    {categories.length > 0 && (
                        <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16,
                        }}>
                            {categories.map((cat) => (
                                <div key={cat.category} style={{
                                    background: C.faint, border: `1px solid ${C.border}`,
                                    borderRadius: 8, padding: '8px 12px', fontFamily: FONT,
                                }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                                        {cat.category}
                                    </div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                        {cat.quantity} &middot; {fmt(cat.revenue, currency)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <Table>
                        <thead>
                            <tr>
                                <Th>{t('product') || 'Product'}</Th>
                                <Th>{t('category') || 'Category'}</Th>
                                <Th align="right">{t('quantity') || 'Quantity'}</Th>
                                <Th align="right">{t('revenue') || 'Revenue'}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((p) => (
                                <React.Fragment key={p.product_code}>
                                    <Row>
                                        <Td bold>
                                            {p.product_name}
                                            {(p.addons || []).length > 0 && (
                                                <button
                                                    onClick={() => toggleAddons(p.product_code)}
                                                    aria-expanded={Boolean(openAddons[p.product_code])}
                                                    aria-label={`${p.product_name}: ${p.addons.length} ${t('add_ons') || 'add-ons'}`}
                                                    style={{
                                                        marginLeft: 8, padding: '2px 8px', borderRadius: 20,
                                                        border: `1px solid ${C.border}`, background: C.faint,
                                                        color: C.muted, fontSize: 10, fontWeight: 700,
                                                        cursor: 'pointer', fontFamily: FONT,
                                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                                        verticalAlign: 'middle',
                                                    }}
                                                >
                                                    <span style={{
                                                        display: 'inline-block', fontSize: 8,
                                                        transform: openAddons[p.product_code] ? 'rotate(90deg)' : 'none',
                                                        transition: 'transform 0.15s',
                                                    }}>&#9654;</span>
                                                    {p.addons.length} {t('add_ons') || 'add-ons'}
                                                </button>
                                            )}
                                        </Td>
                                        <Td>{p.category}</Td>
                                        <Td align="right" bold>{p.quantity}</Td>
                                        <Td align="right">{fmt(p.revenue, currency)}</Td>
                                    </Row>
                                    {/* Add-ons sold with this product, indented one
                                        level. An add-on attached to another add-on
                                        rolls up to its root product, so this list is
                                        flat by design — the owner is asking what went
                                        on the burger, not what was attached to what. */}
                                    {openAddons[p.product_code] && (p.addons || []).map((a) => (
                                        <Row key={`${p.product_code}-${a.name}`}>
                                            <Td>
                                                <span style={{
                                                    display: 'inline-block', paddingLeft: 22,
                                                    color: C.muted,
                                                }}>
                                                    <span style={{ color: C.mutedLight, marginRight: 6 }}>&#8627;</span>
                                                    {a.name}
                                                </span>
                                            </Td>
                                            <Td>{''}</Td>
                                            <Td align="right">{a.quantity}</Td>
                                            <Td align="right">{fmt(a.revenue, currency)}</Td>
                                        </Row>
                                    ))}
                                </React.Fragment>
                            ))}
                            {unattached.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={4} style={{
                                            padding: '14px 10px 6px', fontSize: 11, fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: 0.8,
                                            color: C.muted, borderBottom: `1px solid ${C.borderLight}`,
                                        }}>
                                            {t('eod_unattached_addons') || 'Not attached to a product'}
                                        </td>
                                    </tr>
                                    {unattached.map((m) => (
                                        <Row key={`un-${m.name}`}>
                                            <Td>
                                                <span style={{ display: 'inline-block', paddingLeft: 22, color: C.muted }}>
                                                    <span style={{ color: C.mutedLight, marginRight: 6 }}>&#8627;</span>
                                                    {m.name}
                                                </span>
                                            </Td>
                                            <Td>{''}</Td>
                                            <Td align="right">{m.quantity}</Td>
                                            <Td align="right">{fmt(m.revenue, currency)}</Td>
                                        </Row>
                                    ))}
                                </>
                            )}
                            <tr>
                                <Td bold>{t('total') || 'Total'}</Td>
                                <Td>{''}</Td>
                                <Td align="right" bold>{totalQuantity}</Td>
                                <Td align="right" bold>{fmt(totalRevenue, currency)}</Td>
                            </tr>
                        </tbody>
                    </Table>
                </>
            )}
        </Card>
    );
};

// ─── Payment methods / dining options ────────────────────────────────────────

const CountTotalTable = ({ rows, labelKey, labelHeader, currency, t, emptyMessage }) => (
    rows.length === 0 ? <SectionEmpty message={emptyMessage} /> : (
        <Table>
            <thead>
                <tr>
                    <Th>{labelHeader}</Th>
                    <Th align="right">{t('count') || 'Count'}</Th>
                    <Th align="right">{t('total') || 'Total'}</Th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <Row key={row[labelKey]}>
                        <Td bold>{row[labelKey]}</Td>
                        <Td align="right">{row.count}</Td>
                        <Td align="right" bold>{fmt(row.total, currency)}</Td>
                    </Row>
                ))}
            </tbody>
        </Table>
    )
);

// ─── Ingredient movement ─────────────────────────────────────────────────────

const IngredientTable = ({ rows, t }) => (
    <Table>
        <thead>
            <tr>
                <Th>{t('ingredient') || 'Ingredient'}</Th>
                <Th align="right">{t('quantity') || 'Quantity'}</Th>
                <Th>{t('unit') || 'Unit'}</Th>
            </tr>
        </thead>
        <tbody>
            {rows.map((row) => (
                <Row key={row.inventory_id}>
                    <Td bold>{row.name}</Td>
                    <Td align="right" bold>{row.quantity}</Td>
                    <Td>{row.unit}</Td>
                </Row>
            ))}
        </tbody>
    </Table>
);

const IngredientMovement = ({ ingredients, t }) => {
    // Waste and adjustments are shown apart from usage on purpose: "90g beans
    // used" must not quietly mean "80g used and 10g binned".
    const groups = [
        { key: 'usage', label: t('ingredients_used') || 'Used' },
        { key: 'waste', label: t('ingredients_wasted') || 'Wasted' },
        { key: 'adjustments', label: t('ingredient_adjustments') || 'Adjustments' },
        { key: 'purchases', label: t('ingredients_purchased') || 'Purchased' },
    ].filter(g => (ingredients[g.key] || []).length > 0);

    return (
        <Card style={{ marginBottom: 24 }}>
            <SectionHeader title={t('ingredient_movement') || 'Ingredient Movement'} />
            {groups.length === 0 ? (
                <SectionEmpty message={t('eod_no_stock_movement') || 'No stock movement recorded for this business day.'} />
            ) : groups.map((group) => (
                <div key={group.key} style={{ marginBottom: 16 }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.8, color: C.muted, marginBottom: 8, fontFamily: FONT,
                    }}>{group.label}</div>
                    <IngredientTable rows={ingredients[group.key]} t={t} />
                </div>
            ))}
        </Card>
    );
};

// ─── Main ────────────────────────────────────────────────────────────────────

const EodSalesSections = ({ report, currency, t, isMobile }) => {
    const [showModifiers, setShowModifiers] = useState(false);

    if (!report) return null;

    const {
        totals = {}, products = [], categories = [], payment_methods: paymentMethods = [],
        dining_options: diningOptions = [], modifiers = [], expenses = [], ingredients,
        unattached_modifiers: unattachedModifiers = [],
    } = report;

    return (
        <div style={{ marginTop: 28 }}>
            <SectionHeader title={t('sales_breakdown') || 'Sales Breakdown'} />

            <DaySummary totals={totals} currency={currency} t={t} isMobile={isMobile} />

            <SoldSection
                products={products}
                categories={categories}
                unattached={unattachedModifiers}
                currency={currency}
                t={t}
            />

            <div className="portal-grid-2" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24,
            }}>
                <Card>
                    <SectionHeader title={t('payment_methods') || 'Payment Methods'} />
                    <CountTotalTable
                        rows={paymentMethods}
                        labelKey="method"
                        labelHeader={t('payment_method') || 'Method'}
                        currency={currency}
                        t={t}
                        emptyMessage={t('eod_no_payments') || 'No payments recorded.'}
                    />
                </Card>
                <Card>
                    <SectionHeader title={t('dining_options') || 'Dining Options'} />
                    <CountTotalTable
                        rows={diningOptions}
                        labelKey="option"
                        labelHeader={t('dining_option') || 'Option'}
                        currency={currency}
                        t={t}
                        emptyMessage={t('eod_no_dining_options') || 'No dining options recorded.'}
                    />
                </Card>
            </div>

            <Card style={{ marginBottom: 24 }}>
                <SectionHeader title={t('modifiers') || 'Modifiers'} />
                {modifiers.length === 0 ? (
                    <SectionEmpty message={t('eod_no_modifiers') || 'No add-ons were sold on this business day.'} />
                ) : (
                    <>
                        <Disclosure
                            label={`${modifiers.length} ${modifiers.length === 1 ? (t('modifier') || 'modifier') : (t('modifiers') || 'modifiers')}`}
                            open={showModifiers}
                            onToggle={() => setShowModifiers(!showModifiers)}
                        />
                        {showModifiers && (
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>{t('modifier') || 'Modifier'}</Th>
                                        <Th align="right">{t('quantity') || 'Quantity'}</Th>
                                        <Th align="right">{t('revenue') || 'Revenue'}</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modifiers.map((m) => (
                                        <Row key={m.name}>
                                            <Td bold>{m.name}</Td>
                                            <Td align="right" bold>{m.quantity}</Td>
                                            <Td align="right">{fmt(m.revenue, currency)}</Td>
                                        </Row>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </>
                )}
            </Card>

            {/* Absent entirely for a tenant whose plan has no inventory. */}
            {ingredients && <IngredientMovement ingredients={ingredients} t={t} />}

            <Card style={{ marginBottom: 24 }}>
                <SectionHeader title={t('expenses') || 'Expenses'} />
                {expenses.length === 0 ? (
                    <SectionEmpty message={t('eod_no_expenses') || 'No expenses recorded for this business day.'} />
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>{t('category') || 'Category'}</Th>
                                <Th>{t('item') || 'Item'}</Th>
                                <Th align="right">{t('total') || 'Total'}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((e, i) => (
                                <Row key={`${e.category}-${e.product}-${i}`}>
                                    <Td bold>{e.category}</Td>
                                    <Td>{e.product}</Td>
                                    <Td align="right" bold>{fmt(e.total, currency)}</Td>
                                </Row>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card>
        </div>
    );
};

export default EodSalesSections;
